import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    Avatar,
    LinearProgress,
    Tabs,
    Tab,
    Chip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    Bolt as BoltIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../utils/responsiveMedia';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';

/**
 * ProfessionalSelectionStep - Étape 3 du flux de réservation
 * 
 * Permet de sélectionner un professionnel pour chaque prestation.
 */
const ProfessionalSelectionStep = ({
    salonId,
    selectedServices = [],
    employees = [], // Liste complète des employés
    onContinue,
    onBack,
    onCancel,
    currentStep = 3,
    totalSteps = 5
}) => {
    const { t } = useTranslation();
    const { tokens: theme } = useSalonPublicTheme();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'lg'));
    const isLargeTablet = useMediaQuery(muiTheme.breakpoints.between('lg', 'xl'));
    // Map serviceId -> employeeId (ou 'any')
    const [selectionMap, setSelectionMap] = useState({});

    // Initialisation de la map si vide
    useEffect(() => {
        const initialMap = {};
        selectedServices.forEach(s => {
            const sId = s.service_id || s.id;
            if (!sId) return; // Should not happen

            if (!selectionMap[sId]) {
                initialMap[sId] = 'any'; // Par défaut "Peu importe"
            } else {
                initialMap[sId] = selectionMap[sId];
            }
        });
        setSelectionMap(prev => ({ ...prev, ...initialMap }));
    }, [selectedServices]);

    const handleSelectpro = (serviceId, employeeId) => {
        setSelectionMap(prev => ({
            ...prev,
            [serviceId]: employeeId
        }));
    };

    const handleContinue = () => {
        onContinue(selectionMap);
    };

    // Helper pour savoir si pro sélectionné pour ce service
    const isSelected = (serviceId, empId) => {
        return selectionMap[serviceId] === empId;
    };

    // Helper pour obtenir les employés compatibles avec un service donné
    const getCompatibleEmployees = (service) => {
        if (!service) return [];
        return employees.filter(emp => {
            if (!emp.service_ids) return true;
            // Assumons que service_ids contient les IDs des services qu'il fait
            // service_ids peut être [2, 3] et service.service_id est 2
            return emp.service_ids.includes(service.service_id);
        });
    };

    return (
        <Container
            data-testid="professional-selection-theme-root"
            maxWidth="xl"
            style={{ fontFamily: theme.typography.fontFamily.sans.join(', ') }}
            sx={{
            py: { xs: 0, md: 0 },
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            [HIGH_DENSITY_TABLET_MEDIA]: {
                maxWidth: 'min(94vw, 1500px)',
                minHeight: 'auto',
                pb: 8,
            },
        }}>
            {/* Bouton Retour */}
            <Box sx={{ 
                mb: 0, // Suppression de l'espace en dessous du bouton
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
                maxWidth: 1200,
                mx: '0',
                px: { xs: 0, md: 0 },
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    maxWidth: 1440,
                },
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{
                        color: theme.colors.gray[600],
                        textTransform: 'none',
                        fontWeight: 600,
                        pl: 0,
                        '&:hover': {
                            backgroundColor: theme.colors.gray[100],
                        }
                    }}
                >
                    {t('client_services.back')}
                </Button>
            </Box>

            {/* Contenu principal */}
            <Box sx={{ 
                flex: 1,
                maxWidth: 1200, 
                mx: 'auto',
                width: '100%',
                px: { xs: 2, md: 0 },
                pb: { xs: 12, md: 4 },
                pt: 1, // Petit espace entre le bouton et le contenu
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    maxWidth: 1440,
                    flex: '0 0 auto',
                    pb: 2,
                },
            }}>

                {/* Liste des Services (Itération sur tous les services sélectionnés) */}
                {selectedServices.map((service, index) => {
                    const compatibleEmployees = getCompatibleEmployees(service);
                    const sId = service.service_id || service.id; // Robust ID

                    return (
                        <Box key={sId} sx={{ mb: 6 }}>
                            {/* Titre du Service (Affiché seulement si plus d'un service ou pour clarté) */}
                            {selectedServices.length > 0 && (
                                <Box sx={{ mb: 3, pb: 1, borderBottom: `1px dashed ${theme.colors.gray[200]}` }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.gray[800], display: 'flex', alignItems: 'center' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 24, height: 24, borderRadius: '50%', backgroundColor: theme.colors.primary[100],
                                            color: theme.colors.primary[700], fontSize: 12, marginRight: 12
                                        }}>
                                            {index + 1}
                                        </span>
                                        {service.name}
                                    </Typography>
                                </Box>
                            )}

                            {/* Grille des Pros pour ce service */}

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'stretch',
                                    alignContent: 'flex-start',
                                    gap: { xs: '16px', md: '24px' },
                                    '& > .pro-card': {
                                        flexGrow: 0,
                                        flexShrink: 0,
                                        flexBasis: {
                                            xs: 'calc(50% - 16px)',
                                            sm: 'calc(50% - 16px)',
                                            md: 'calc(25% - 24px)'
                                        },
                                        [HIGH_DENSITY_TABLET_MEDIA]: {
                                            flexBasis: 'calc(25% - 24px)',
                                        },
                                    }
                                }}
                            >
                                {/* Carte "Peu importe" */}
                                <Paper
                                    className="pro-card"
                                    elevation={0}
                                    onClick={() => handleSelectpro(sId, 'any')}
                                    sx={{
                                        p: { xs: 1.5, md: 3 },
                                        [HIGH_DENSITY_TABLET_MEDIA]: {
                                            p: 3.5,
                                        },
                                        borderRadius: 4,
                                        border: isSelected(sId, 'any') ? `2px solid ${theme.colors.primary[500]}` : `1px solid ${theme.colors.gray[200]}`,
                                        backgroundColor: isSelected(sId, 'any') ? theme.colors.primary[50] : theme.colors.background.primary,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxSizing: 'border-box',
                                        '&:hover': {
                                            borderColor: theme.colors.primary[500],
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        width: 64, height: 64, borderRadius: '50%',
                                        bgcolor: theme.salon.primary,
                                        color: theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mb: 2, boxShadow: `0 4px 10px ${alpha(theme.salon.primary, 0.3)}`
                                    }}>
                                        <BoltIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{t('client_services.any_employee')}</Typography>
                                    <Typography variant="body2" sx={{ color: theme.colors.primary[500], fontWeight: 600, mb: 1 }}>{t('client_services.first_available')}</Typography>
                                    <Typography variant="caption" sx={{ color: theme.colors.gray[400], textAlign: 'center' }}>
                                        {t('client_services.quick_appointment_recommended')}
                                    </Typography>
                                </Paper>


                                {/* Liste des employés compatibles pour ce service */}
                                {compatibleEmployees.map(emp => {
                                    const userDetails = emp.user_details || {};
                                    const fullName = userDetails.full_name ||
                                        (userDetails.first_name && userDetails.last_name ? `${userDetails.first_name} ${userDetails.last_name}` : t('client_services.expert'));
                                    const photo = userDetails.profile_photo;
                                    const role = emp.team_name || t('client_services.team');

                                    const empId = emp.team_member_id || emp.id;

                                    return (

                                        <Paper
                                            key={`${sId}-${empId}`}
                                            className="pro-card"
                                            elevation={0}
                                            onClick={() => handleSelectpro(sId, empId)}
                                            sx={{
                                               p: { xs: 1.5, md: 3 },
                                                [HIGH_DENSITY_TABLET_MEDIA]: {
                                                    p: 3.5,
                                                },
                                                borderRadius: 4,
                                                border: isSelected(sId, empId) ? `2px solid ${theme.colors.primary[500]}` : `1px solid ${theme.colors.gray[200]}`,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                boxSizing: 'border-box',
                                                '&:hover': {
                                                    borderColor: theme.colors.primary[500],
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                                }
                                            }}
                                        >
                                            <Avatar
                                                src={photo}
                                                alt={fullName}
                                                sx={{
                                                    width: { xs: 60, md: 80 },
                                                    height: { xs: 60, md: 80 },
                                                    mb: { xs: 1, md: 2 }
                                                }}
                                            />
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, textAlign: 'center' }}>
                                                {fullName}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.colors.primary[500], fontWeight: 500, mb: 2 }}>
                                                {role}
                                            </Typography>

                                            {/* Dispo removed requested */}
                                        </Paper>
                                    );
                                })}
                            </Box>

                        </Box>
                    );
                })}

            </Box>

            <Box
                sx={{
                    display: 'none',
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    maxWidth: 1440,
                    width: '100%',
                    mx: 'auto',
                    mt: 1,
                    pb: 8,
                },
                }}
            >
                <Button
                    variant="contained"
                    onClick={handleContinue}
                    endIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)', ml: 0.5 }} />}
                    sx={{
                        minWidth: 240,
                        fontWeight: 700,
                        fontSize: '1.2rem',
                    }}
                >
                    Continuer
                </Button>
            </Box>

            {/* Bouton fixe en bas à droite */}
            <Box sx={{
                position: 'fixed',
                bottom: { xs: 16, sm: isTablet ? 20 : isLargeTablet ? 16 : 24 },
                right: { xs: 16, sm: isTablet ? 20 : isLargeTablet ? 16 : 24 },
                zIndex: 9999,
                display: 'flex',
                justifyContent: 'flex-end',
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    display: 'none',
                },
                '& .MuiButton-root': {
                    minWidth: 'auto',
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', sm: isTablet ? '0.95rem' : isLargeTablet ? '0.88rem' : '1rem' },
                    [HIGH_DENSITY_TABLET_MEDIA]: {
                        minWidth: 220,
                        fontSize: '1.2rem',
                    },
                }
            }}>
                <Button
                    variant="contained"
                    onClick={handleContinue}
                    endIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)', ml: 0.5 }} />}
                >
                    Continuer
                </Button>
            </Box>
        </Container>
    );
};

export default ProfessionalSelectionStep;
